import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Scale, FileCheck2, Calculator, ShieldCheck, Building2, Mail, Lock } from "lucide-react";
import { useLogin, useVerifyTwoFactorLogin } from "@/api/auth";
import { getApiErrorMessage } from "@/api/client";

/**
 * Panel izquierdo de marca, compartido entre el login y el paso de 2FA
 * (rediseño visual general — petición explícita del usuario: que el
 * sistema se sienta como el de una gran firma contable/jurídica/de
 * trámites, no una plantilla genérica). Estructura tipo "split screen"
 * inspirada en la referencia que mandó el usuario, pero con branding e
 * íconos administrativos (balanza, contabilidad, documentos, auditoría) en
 * vez de inventario/logística.
 */
function BrandPanel() {
  const features = [
    { icon: Calculator, text: "Contabilidad, nómina y facturación electrónica" },
    { icon: Scale, text: "Gestión jurídica y trámites de principio a fin" },
    { icon: FileCheck2, text: "Documentos, firmas y expedientes centralizados" },
    { icon: ShieldCheck, text: "Auditoría, roles y seguridad de nivel empresarial" },
  ];

  return (
    <div className="relative hidden lg:flex lg:w-1/2 flex-col justify-between overflow-hidden bg-gradient-to-br from-brand-950 via-brand-900 to-brand-800 px-12 py-12 text-white">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "28px 28px",
        }}
      />
      <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-accent-500/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-brand-400/10 blur-3xl" />

      <div className="relative z-10 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-500 shadow-lg shadow-accent-900/30">
          <Building2 className="h-6 w-6 text-brand-950" strokeWidth={2.25} />
        </div>
        <div>
          <p className="text-lg font-semibold leading-tight tracking-tight">ERP Firma Profesional</p>
          <p className="text-xs font-medium uppercase tracking-widest text-brand-300">
            Suite empresarial integral
          </p>
        </div>
      </div>

      <div className="relative z-10 max-w-md">
        <h2 className="text-3xl font-semibold leading-tight tracking-tight text-white">
          La plataforma que administra su firma de principio a fin.
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-brand-200">
          Contable, jurídica y de trámites: un solo sistema para operar, cumplir y crecer,
          con el mismo nivel de solidez que exige una firma grande.
        </p>

        <ul className="mt-8 space-y-4">
          {features.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10 ring-1 ring-white/10">
                <Icon className="h-4.5 w-4.5 text-accent-300" size={18} />
              </span>
              <span className="text-sm text-brand-100">{text}</span>
            </li>
          ))}
        </ul>
      </div>

      <p className="relative z-10 text-xs text-brand-400">
        © {new Date().getFullYear()} ERP Firma Profesional — Acceso exclusivo para personal autorizado.
      </p>
    </div>
  );
}

interface LoginFormValues {
  email: string;
  password: string;
  organizationSlug?: string;
}

interface TwoFactorFormValues {
  code: string;
}

export default function LoginPage() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>();
  const login = useLogin();
  const navigate = useNavigate();
  const location = useLocation();
  const [needsOrgSlug, setNeedsOrgSlug] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);

  const from = (location.state as { from?: string } | null)?.from;

  const onSubmit = async (values: LoginFormValues) => {
    try {
      const result = await login.mutateAsync(values);
      if (result.requiresTwoFactor) {
        setMfaToken(result.mfaToken);
        return;
      }
      // Los usuarios del Portal del cliente (Fase 19) siempre van a /portal,
      // sin importar el "from": no tienen acceso a las rutas internas.
      const destination = result.user.clientId ? "/portal" : from ?? "/dashboard";
      navigate(destination, { replace: true });
    } catch (error) {
      const message = getApiErrorMessage(error);
      if (message.toLowerCase().includes("varias organizaciones")) {
        setNeedsOrgSlug(true);
      }
      toast.error(message);
    }
  };

  if (mfaToken) {
    return <TwoFactorStep mfaToken={mfaToken} onBack={() => setMfaToken(null)} from={from} />;
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <BrandPanel />

      <div className="flex w-full flex-1 items-center justify-center px-4 py-12 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex flex-col items-center text-center lg:hidden">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-800 shadow-sm">
              <Building2 className="h-6 w-6 text-accent-400" strokeWidth={2.25} />
            </div>
            <p className="mt-3 text-lg font-semibold tracking-tight text-gray-900">ERP Firma Profesional</p>
          </div>

          <p className="section-eyebrow">Acceso al sistema</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-gray-900">Iniciar sesión</h1>
          <p className="mt-1 text-sm text-gray-500">Ingrese sus credenciales para continuar.</p>

          <form className="mt-8 space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="email">
                Correo electrónico
              </label>
              <div className="relative mt-1.5">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="tu@correo.com"
                  className="input-field pl-9"
                  {...register("email", { required: "El correo es requerido" })}
                />
              </div>
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="password">
                Contraseña
              </label>
              <div className="relative mt-1.5">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="input-field pl-9"
                  {...register("password", { required: "La contraseña es requerida" })}
                />
              </div>
              {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>}
            </div>

            {needsOrgSlug && (
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="organizationSlug">
                  Organización
                </label>
                <input
                  id="organizationSlug"
                  type="text"
                  placeholder="código de su firma"
                  className="input-field mt-1.5"
                  {...register("organizationSlug", { required: needsOrgSlug ? "Indique la organización" : false })}
                />
              </div>
            )}

            <div className="flex items-center justify-end">
              <Link to="/forgot-password" className="text-sm font-medium text-brand-700 hover:text-brand-800">
                ¿Olvidó su contraseña?
              </Link>
            </div>

            <button type="submit" className="btn-primary w-full py-3" disabled={isSubmitting || login.isPending}>
              {login.isPending ? "Ingresando..." : "Ingresar"}
            </button>

            <p className="flex items-center justify-center gap-1.5 pt-2 text-xs text-gray-400">
              <ShieldCheck className="h-3.5 w-3.5" />
              Solo personal autorizado de la organización
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

/**
 * Segundo paso del login cuando la cuenta tiene 2FA activado (ver
 * `docs/PROGRESO.md`, continuación posterior a la Fase 26). Acepta tanto
 * un código de 6 dígitos de la app autenticadora como un código de
 * recuperación de un solo uso ("XXXXX-XXXXX") — el backend distingue
 * cuál es cuál (`twoFactor.service.ts`).
 */
function TwoFactorStep({ mfaToken, onBack, from }: { mfaToken: string; onBack: () => void; from?: string }) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<TwoFactorFormValues>();
  const verifyTwoFactor = useVerifyTwoFactorLogin();
  const navigate = useNavigate();

  const onSubmit = async (values: TwoFactorFormValues) => {
    try {
      const result = await verifyTwoFactor.mutateAsync({ mfaToken, code: values.code.trim() });
      const destination = result.user.clientId ? "/portal" : from ?? "/dashboard";
      navigate(destination, { replace: true });
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <BrandPanel />

      <div className="flex w-full flex-1 items-center justify-center px-4 py-12 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex flex-col items-center text-center lg:hidden">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-800 shadow-sm">
              <Building2 className="h-6 w-6 text-accent-400" strokeWidth={2.25} />
            </div>
            <p className="mt-3 text-lg font-semibold tracking-tight text-gray-900">ERP Firma Profesional</p>
          </div>

          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50">
            <ShieldCheck className="h-5.5 w-5.5 text-brand-700" size={22} />
          </div>

          <p className="section-eyebrow mt-4 text-center">Seguridad de la cuenta</p>
          <h1 className="mt-1 text-center text-2xl font-semibold tracking-tight text-gray-900">Verificación en dos pasos</h1>
          <p className="mt-1 text-center text-sm text-gray-500">
            Ingrese el código de 6 dígitos de su aplicación autenticadora, o uno de sus códigos de recuperación.
          </p>

          <form className="mt-8 space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="code">
                Código de verificación
              </label>
              <input
                id="code"
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                autoFocus
                placeholder="123456"
                className="input-field mt-1.5 text-center tracking-[0.3em]"
                {...register("code", { required: "El código es requerido" })}
              />
              {errors.code && <p className="mt-1 text-xs text-red-600">{errors.code.message}</p>}
            </div>

            <button type="submit" className="btn-primary w-full py-3" disabled={isSubmitting || verifyTwoFactor.isPending}>
              {verifyTwoFactor.isPending ? "Verificando..." : "Verificar"}
            </button>

            <button type="button" className="w-full text-center text-sm font-medium text-gray-500 hover:text-brand-700" onClick={onBack}>
              Volver al inicio de sesión
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
